import { NextSeo } from 'next-seo';
import React, { CSSProperties } from 'react';
import HubspotMeeting from '../../components/elements/HubspotMeeting';
import BannerTemplate from '../../components/ui/Banner';
import BlogPostList from '../../components/ui/BlogPostList';
import Gallery from '../../components/ui/Gallery';
import SectionQuote from '../../components/ui/SectionQuote';
import Richtext from '../../components/ui/Richtext';
import { Blog } from '../../types/schema';
import {
  blog,
  description,
  gallery,
  generateMockContent,
  mockTestimonials,
  richtext,
  sectionBackgroundImageRichText,
  sectionImageRichText,
  sectionTextButtons,
  sectionCards,
  sectionTitleItemList,
  MockSectionImageRichTextList,
  MockTechnologies,
  MockEventListProps,
  formDefault,
  formFeedbackQuestionAllTypes,
  formWizard,
  formWizardConditional,
  formWizardPromptResultWithQuestion,
} from './data';
import { SectionCards } from '../../components/ui/SectionCards';
import { ComponentWrapper, SingleComponentWrapper, Wrapper } from './styles';
import SectionTextButtons from '../../components/ui/SectionTextButtons';
import SectionBackgroundImageRichText from '../../components/ui/SectionBackgroundImageRichText';
import SectionImageRichText from '../../components/ui/SectionImageRichText';
import SectionTextColumns from '../../components/ui/SectionTextColumns';
import UserTestimony from '../../components/ui/UserTestimony';
import SectionTitleItemList from '../../components/ui/SectionTitleItemList';
import { getBackgroundColor, getTextColor } from '../../utils/dynamicZone';
import SectionImageRichTextList from '../../components/ui/SectionImageRichTextList';
import TechnologyGrid from '../../components/ui/TechnologyGrid';
import EventList, { EventListProps } from '../../components/ui/EventList';
import ClientComponent from '../../components/ui/ClientComponent/ClientComponent';
import FormView from '../FormView';
import ContactFormTemplate from '../../components/ui/ContactForm/template';

type ContainerProps = {
  title: string;
  components: {
    render: JSX.Element;
    subtitle?: string;
    contentStyles?: CSSProperties;
  }[];
};

const Container: React.FC<ContainerProps> = ({ title, components }) => {
  return (
    <ComponentWrapper>
      <h2>{title}</h2>
      <ul>
        {components.map((component, i) => {
          const props = component.render.props;
          return (
            <li key={i}>
              {component.subtitle && <h3 className="subtitle-li">{component.subtitle}</h3>}
              <SingleComponentWrapper
                backgroundImage={props?.backgroundImage}
                backgroundColor={getBackgroundColor(props)}
                textColor={getTextColor(props)}
                style={component.contentStyles}
              >
                {component.render}
              </SingleComponentWrapper>
            </li>
          );
        })}
      </ul>
    </ComponentWrapper>
  );
};

const StorybookViewComponent: React.FC = () => {
  const handleFormSubmit = async () => {
    return;
  };

  return (
    <>
      <NextSeo noindex title="App Masters - Storybook" />
      <Wrapper>
        <section>
          <h2>Componentes novos:</h2>
          <Container
            title="FormView"
            components={[
              { subtitle: 'type: default', render: <FormView form={formDefault} /> },
              {
                subtitle: 'type: default (todos os tipos de pergunta)',
                render: <FormView form={formFeedbackQuestionAllTypes} />,
              },
              { subtitle: 'type: wizard', render: <FormView form={formWizard} /> },
              {
                subtitle: 'type: wizard (conditional sections)',
                render: <FormView form={formWizardConditional} />,
              },
              {
                subtitle: 'type: wizard (promptQuestion + promptResult)',
                render: <FormView form={formWizardPromptResultWithQuestion} />,
              },
            ]}
          />
          <Container
            title="SectionImageRichTextList"
            components={[
              {
                subtitle: 'mediaType: icon',
                render: <SectionImageRichTextList {...MockSectionImageRichTextList.icon} />,
              },
              {
                subtitle: 'mediaType: image',
                render: <SectionImageRichTextList {...MockSectionImageRichTextList.image} />,
              },
            ]}
          />
          <Container
            title="SectionTitleItemList"
            components={[
              {
                subtitle: 'contentOrientation: row, listType: accordion',
                render: <SectionTitleItemList {...sectionTitleItemList.first} />,
              },
              {
                subtitle: 'contentOrientation: row, listType: numeric',
                render: <SectionTitleItemList {...sectionTitleItemList.second} />,
              },
              {
                subtitle: 'contentOrientation: column, listType: accordion',
                render: <SectionTitleItemList {...sectionTitleItemList.third} />,
              },
            ]}
          />
          <Container
            title="SectionCards"
            components={[
              {
                subtitle: 'CardPosition: inline, BackgroundColor: blue, ShowNumber: true',
                render: <SectionCards {...sectionCards.first} />,
              },
              {
                subtitle:
                  'CardPosition: inline, BackgroundColor: darkBlue, showNumber: true, Just cards without icon',
                render: <SectionCards {...sectionCards.second} />,
              },
              {
                subtitle: 'CardPosition: left, BackgroundColor: not-selected, showNumber: true',
                render: <SectionCards {...sectionCards.third} />,
              },
              {
                subtitle: 'CardPosition: right, BackgroundCoor: blue, showNumber: false',
                render: <SectionCards {...sectionCards.fourth} />,
              },
            ]}
          />
          <Container
            title="Testimonials"
            components={[
              {
                render: (
                  <UserTestimony
                    {...mockTestimonials}
                    backgroundColor="lightGradient"
                    columns={3}
                  />
                ),
                subtitle: 'White: 3 Colunas',
              },
              {
                render: (
                  <UserTestimony
                    {...mockTestimonials}
                    cardBackground="darkGradient"
                    backgroundColor="lightGradient"
                    columns={3}
                  />
                ),
                subtitle: 'Main Gradient: 3 Colunas',
              },
              {
                render: (
                  <UserTestimony {...mockTestimonials} cardBackground="lightGradient" columns={1} />
                ),
                subtitle: 'Light Gradient: 1 Coluna',
              },
              {
                render: (
                  <UserTestimony
                    {...mockTestimonials}
                    cardBackground="darkBlue"
                    backgroundColor="lightGradient"
                    columns={1}
                  />
                ),
                subtitle: 'Dark Blue: 3 Coluna',
              },
            ]}
          />
          <Container
            title="SectionTextButtons"
            components={[
              {
                subtitle: 'alignment: center, backgroundColor: lightGradient',
                render: (
                  <SectionTextButtons {...sectionTextButtons} backgroundColor="lightGradient" />
                ),
              },
              {
                subtitle: 'alignment: left, backgroundColor: lightGradient',
                render: (
                  <SectionTextButtons {...sectionTextButtons} backgroundColor="lightGradient" />
                ),
              },
              {
                subtitle: 'alignment: right, backgroundColor: darkGradient',
                render: (
                  <SectionTextButtons {...sectionTextButtons} backgroundColor="darkGradient" />
                ),
              },
            ]}
          />
          <Container
            title="HubspotMeeting"
            components={[
              {
                subtitle: 'background: primary',
                render: (
                  <HubspotMeeting
                    slug="tiago47"
                    background="primary"
                    title="Meeting Title"
                    description="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum odio urna, volutpat ac cursus id, malesuada et augue. Curabitur faucibus neque vel est maximus fringilla. Nunc interdum, leo ac tristique venenatis, enim ligula egestas tellus, imperdiet mollis ex justo ac nisl. Integer vitae dui orci. Duis ultricies, velit a imperdiet porttitor, augue purus iaculis est, eu posuere eros urna id quam. In at accumsan velit. Nullam mollis sed ante in euismod. Sed auctor ipsum odio, ac rhoncus odio bibendum et. Suspendisse potenti. Vivamus interdum erat vitae turpis mattis, eu varius neque suscipit."
                  />
                ),
              },
            ]}
          />
          <Container
            title="SectionQuote"
            components={[{ render: <SectionQuote text={description} /> }]}
          />
          <Container
            title="SectionBackgroundImageRichText"
            components={[
              {
                subtitle: 'textPosition: left',
                render: (
                  <SectionBackgroundImageRichText {...sectionBackgroundImageRichText.first} />
                ),
              },
              {
                subtitle: 'textPosition: center',
                render: (
                  <SectionBackgroundImageRichText {...sectionBackgroundImageRichText.second} />
                ),
              },
              {
                subtitle: 'textPosition: right',
                render: (
                  <SectionBackgroundImageRichText {...sectionBackgroundImageRichText.third} />
                ),
              },
            ]}
          />
          <Container
            title="SectionTextColumns"
            components={[
              {
                render: (
                  <SectionTextColumns
                    backgroundColor="darkGradient"
                    content={[
                      generateMockContent({
                        icon: 'https://cdn-icons-png.flaticon.com/512/1789/1789706.png',
                        linkDestination: undefined,
                      }),
                      generateMockContent({
                        icon: 'https://www.nicepng.com/png/full/129-1293351_coffee-cup-icon-coffee-cup-icon-white.png',
                        linkText: 'Contact us',
                      }),
                      generateMockContent({}),
                      generateMockContent({}),
                    ]}
                  />
                ),
                subtitle: 'Background Color: darkGradient',
              },
              {
                render: (
                  <SectionTextColumns
                    backgroundColor="darkBlue"
                    content={[
                      generateMockContent({}),
                      generateMockContent({}),
                      generateMockContent({}),
                    ]}
                  />
                ),
                subtitle: 'Background Color: darkBlue',
              },
            ]}
          />
          <Container
            title="ContactFormTemplate"
            components={[
              {
                subtitle: 'background: primary',
                render: (
                  <ContactFormTemplate
                    onSubmit={handleFormSubmit}
                    loading={false}
                    error={false}
                    locale="pt"
                    sitekey={process.env.NEXT_PUBLIC_API_KEY_CAPTCHA as string}
                    success={false}
                    setError={() => undefined}
                    isLookingForJob={false}
                    buttonText="Enviar"
                    background="primary"
                    title="Quer receber o relatório completo?"
                    description="Temos algumas informações a mais para te apresentar"
                    showTermsCheckBox={false}
                  />
                ),
              },
              {
                subtitle: 'background: white',
                render: (
                  <ContactFormTemplate
                    onSubmit={handleFormSubmit}
                    loading={false}
                    error={false}
                    locale="pt"
                    sitekey={process.env.NEXT_PUBLIC_API_KEY_CAPTCHA as string}
                    success={false}
                    setError={() => undefined}
                    isLookingForJob={false}
                    buttonText="Enviar"
                    background="white"
                    title="Quer receber o relatório completo?"
                    description="Temos algumas informações a mais para te apresentar"
                    showTermsCheckBox={false}
                  />
                ),
              },
            ]}
          />
          <Container
            title="SectionImageRichText"
            components={[
              {
                render: <SectionImageRichText {...sectionImageRichText.first} />,
                subtitle: 'imageFit: absolutFull, imageProportion: half',
                contentStyles: { padding: '0px' },
              },
              {
                render: <SectionImageRichText {...sectionImageRichText.second} />,
                subtitle: 'imageFit: absolutBottom, imageProportion: half',
                contentStyles: { paddingTop: '0px' },
              },
              {
                render: <SectionImageRichText {...sectionImageRichText.third} />,
                subtitle: 'imageFit: centerAndFit, imageProportion: half',
              },
              {
                render: <SectionImageRichText {...sectionImageRichText.fourth} />,
                subtitle:
                  'imageFit: centerAndCover, imageProportion: oneThird, backgroundColor: gradient',
              },
            ]}
          />
        </section>
        <section>
          <h2>Componentes antigos:</h2>
          <Container
            title="Banner"
            components={[
              {
                render: <BannerTemplate title="Lorem ipsum" type="primary" />,
                subtitle: 'Type: primary',
              },
              {
                render: (
                  <BannerTemplate
                    title="Lorem ipsum"
                    type="secondary"
                    description="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut"
                    imageUrl="https://res.cloudinary.com/appmasters-io/image/upload/v1670592064/blog_consultoria_desenvolvimento_sistema_0a0b9a8334.png?updated_at=2022-12-09T13:21:14.509Z"
                  />
                ),
                subtitle: 'Type: secondary',
              },
              {
                render: (
                  <BannerTemplate
                    title="Lorem ipsum"
                    type="tertiary"
                    description="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut"
                  />
                ),
                subtitle: 'Type: tertiary',
              },
            ]}
          />
          <Container
            title="BlogPostList"
            components={[
              {
                render: <BlogPostList lastBlogs={[blog, blog] as unknown as Blog[]} title="Blog" />,
              },
            ]}
          />
          <Container
            title="Gallery"
            components={gallery.map(({ props, subtitle }, index) => ({
              render: <Gallery key={index} {...props} />,
              subtitle,
            }))}
          />
          <Container
            title="Richtext"
            components={[
              { render: <Richtext {...richtext.first} />, subtitle: 'Default: false' },
              { render: <Richtext {...richtext.second} />, subtitle: 'Default: true' },
            ]}
          />
          <Container
            title="Technologies Dynamic Zone"
            components={[
              {
                render: (
                  <TechnologyGrid
                    technologies={MockTechnologies}
                    textAlign="left"
                    background="primary"
                    description="Aqui está sendo exibidas todas as tecnologias, com o texto alinhado a esquerda e o background primário."
                  />
                ),
                subtitle: 'Text align: Left, Background: primary',
              },
              {
                render: (
                  <TechnologyGrid
                    technologies={MockTechnologies.filter((t) => t.type === 'Service')}
                    textAlign="right"
                    featured
                    background="transparent"
                    description="Aqui está sendo exibidas apenas as tecnologias de serviço, com o texto alinhado a direita e o background transparente. Está com o modo destaque ativado"
                  />
                ),
                subtitle:
                  'Type: Service, Featured: True, Text align: Right, Background: transparent',
              },
              {
                render: (
                  <TechnologyGrid
                    technologies={MockTechnologies.filter((t) => t.type === 'Library')}
                    textAlign="center"
                    numberOfColumns={4}
                    background="white"
                    description="Aqui está sendo exibidas apenas as tecnologias de biblioteca, com o texto alinhado ao centro e o background branco. A quantidade de colunas é 2."
                  />
                ),
                subtitle: 'Type: Technology, Columns: 2, Text align: Center, Background: white',
              },
              {
                render: (
                  <TechnologyGrid
                    technologies={MockTechnologies}
                    textAlign="center"
                    background="transparent"
                    tiny
                    numberOfColumns={20}
                    description="Variação tiny: ícones pequenos, 20 colunas."
                  />
                ),
                subtitle: 'Tiny: true, Text align: Center, Background: transparent',
              },
              {
                render: (
                  <TechnologyGrid
                    technologies={MockTechnologies}
                    textAlign="center"
                    background="primary"
                    tiny
                    numberOfColumns={9}
                    description="Variação tiny: ícones pequenos, 9 colunas."
                  />
                ),
                subtitle: 'Tiny: true, Text align: Center, Background: primary',
              },
            ]}
          />
          <Container
            title="EventList"
            components={[
              {
                render: <EventList {...(MockEventListProps.first as EventListProps)} />,
                subtitle: `Year: ${new Date().getFullYear()}, sort: Asc`,
              },
              {
                render: <EventList {...(MockEventListProps.second as EventListProps)} />,
                subtitle: `Year: ${new Date().getFullYear()}, sort: Asc, Location: No Brasil, Timing: Future`,
              },
              {
                render: <EventList {...(MockEventListProps.third as EventListProps)} />,
                subtitle: `Year: ${new Date().getFullYear()}, sort: Desc, Location: No Brasil, Timing: Past`,
              },
              {
                render: <EventList {...(MockEventListProps.fourth as EventListProps)} />,
                subtitle: `Year: ${new Date().getFullYear()}, sort: Desc, Location: Fora Do Brasil, Timing: Future`,
              },
              {
                render: <EventList {...(MockEventListProps.fifth as EventListProps)} />,
                subtitle: `Year: ${new Date().getFullYear()}, sort: Asc, Location: Fora Do Brasil, Timing: Past`,
              },
            ]}
          />
        </section>
      </Wrapper>
    </>
  );
};

export default function StorybookView() {
  return (
    <ClientComponent>
      <StorybookViewComponent />
    </ClientComponent>
  );
}
